import React from "react";
import { ArrowRight, CheckCircle, ShieldCheck } from "lucide-react";
import type { CategoryDetails, SubCategory } from "../../../types";

export type TileOverlays = "default" | "overlay" | "popup";

export type ServiceTilesLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ServiceTilesImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
};

export type ServiceTilesCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
  icon?: React.ReactNode;
};

export type ServiceTilesTileCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
};

type ServiceTilesSubCategoryFields = Omit<
  Partial<SubCategory>,
  | "id"
  | "title"
  | "name"
  | "description"
  | "link"
  | "images"
  | "heroImage"
  | "accentColor"
  | "accentDarkColor"
>;

export type ServiceTilesItem = ServiceTilesSubCategoryFields & {
  id: string | number;
  title?: string;
  name?: string;
  overline?: string;
  text?: string;
  description?: string;
  href?: string;
  link?: string;
  image?: ServiceTilesImage;
  images?: ServiceTilesImage[];
  heroImage?: ServiceTilesImage;
  imageUrl?: string;
  imageAlt?: string;
  icon?: React.ReactNode;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
  accentDarkColor?: string;
  cta?: ServiceTilesTileCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
};

export type ServiceTilesProps = {
  service?: CategoryDetails;
  items?: ServiceTilesItem[];
  serviceTileClasses?: string;
  Link?: ServiceTilesLinkComponent;
  variant?: TileOverlays;
  tileHeight?: string;
  tileMinHeight?: number | string;
  mobileTileMinHeight?: number | string;
  tileImageHeight?: number | string;
  brandedOverlay?: boolean;
  overline?: string;
  title?: string;
  text?: string;
  description?: string;
  cta?: ServiceTilesCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
  accentColor?: string;
  backgroundColor?: string;
  panelBackgroundColor?: string;
  maxWidth?: number | string;
  listTitle?: string;
  className?: string;
  ariaLabel?: string;
};

const defaultAccentColor = "#d65a14";
const defaultBackgroundColor = "#fbfaf7";
const defaultPanelBackgroundColor = "#fffdf8";
const defaultMaxWidth = 1180;
const defaultTileImageHeight = 104;
const defaultTileMinHeight = 158;
const defaultMobileTileMinHeight = 70;

const DefaultLink: ServiceTilesLinkComponent = ({ href, children, ...props }) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const serviceTilesCss = `
.service-tiles {
  --service-tiles-accent: ${defaultAccentColor};
  --service-tiles-bg: ${defaultBackgroundColor};
  --service-tiles-panel-bg: ${defaultPanelBackgroundColor};
  --service-tiles-text: #132f2d;
  --service-tiles-muted: #35465a;
  --service-tiles-border: rgba(19, 47, 45, 0.12);
  --service-tiles-rule: rgba(19, 47, 45, 0.14);
  --service-tiles-card-bg: #ffffff;
  --service-tiles-tile-image-height: ${defaultTileImageHeight}px;
  --service-tiles-tile-min-height: ${defaultTileMinHeight}px;
  --service-tiles-mobile-tile-min-height: ${defaultMobileTileMinHeight}px;
  width: 100%;
  background: var(--service-tiles-bg);
  color: var(--service-tiles-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.service-tiles,
.service-tiles * {
  box-sizing: border-box;
}
.service-tiles-shell {
  width: 100%;
  max-width: ${defaultMaxWidth}px;
  margin: 0 auto;
  padding: 28px 18px;
}
.service-tiles-layout {
  display: grid;
  grid-template-columns: minmax(300px, 0.82fr) minmax(0, 1.18fr);
  align-items: stretch;
  gap: 32px;
  overflow: hidden;
  border: 1px solid var(--service-tiles-border);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 48px rgba(19, 47, 45, 0.10);
}
.service-tiles-copy {
  min-width: 0;
  padding: 42px 38px 44px;
  background: var(--service-tiles-panel-bg);
}
.service-tiles-overline {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 20px;
  color: var(--service-tiles-accent);
  font-size: 12px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
}
.service-tiles-overline::before {
  content: "";
  flex: 0 0 34px;
  width: 34px;
  height: 2px;
  border-radius: 999px;
  background: var(--service-tiles-accent);
}
.service-tiles-title {
  max-width: 500px;
  margin: 0;
  color: var(--service-tiles-text);
  font-size: 42px;
  line-height: 1.08;
  font-weight: 850;
  letter-spacing: 0;
}
.service-tiles-description {
  max-width: 430px;
  margin: 18px 0 0;
  color: var(--service-tiles-muted);
  font-size: 16px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.service-tiles-cta {
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 11px;
  margin-top: 30px;
  padding: 13px 24px;
  border-radius: 5px;
  background: var(--service-tiles-accent);
  color: #ffffff;
  font-size: 13px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
  text-decoration: none;
  box-shadow: 0 12px 24px color-mix(in srgb, var(--service-tiles-accent) 28%, transparent);
  transition: transform 160ms ease, filter 160ms ease, box-shadow 160ms ease;
}
.service-tiles-cta:hover,
.service-tiles-cta:focus-visible {
  transform: translateY(-1px);
  filter: brightness(0.95);
  box-shadow: 0 15px 30px color-mix(in srgb, var(--service-tiles-accent) 34%, transparent);
}
.service-tiles-cta:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-tiles-accent) 24%, transparent);
  outline-offset: 4px;
}
.service-tiles-cta svg {
  flex: 0 0 auto;
}
.service-tiles-list {
  margin-top: 34px;
}
.service-tiles-list-heading {
  display: grid;
  grid-template-columns: auto max-content minmax(32px, 1fr);
  align-items: center;
  gap: 13px;
  margin-bottom: 18px;
  color: var(--service-tiles-text);
}
.service-tiles-list-heading-icon {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--service-tiles-muted);
}
.service-tiles-list-heading-text {
  font-size: 12px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
}
.service-tiles-list-heading-rule {
  height: 1px;
  background: var(--service-tiles-rule);
}
.service-tiles-list-items {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.service-tiles-list-link {
  display: grid;
  min-height: 38px;
  grid-template-columns: 20px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 13px;
  border-bottom: 1px solid rgba(19, 47, 45, 0.10);
  color: var(--service-tiles-muted);
  font-size: 14px;
  line-height: 1.35;
  font-weight: 500;
  letter-spacing: 0;
  text-decoration: none;
  transition: color 160ms ease;
}
.service-tiles-list-link:hover,
.service-tiles-list-link:focus-visible {
  color: var(--service-tiles-text);
}
.service-tiles-list-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-tiles-accent) 22%, transparent);
  outline-offset: 3px;
  border-radius: 4px;
}
.service-tiles-list-check {
  color: var(--service-tiles-accent);
}
.service-tiles-list-arrow {
  justify-self: end;
  color: var(--service-tiles-text);
  transition: transform 160ms ease;
}
.service-tiles-list-link:hover .service-tiles-list-arrow,
.service-tiles-list-link:focus-visible .service-tiles-list-arrow {
  transform: translateX(3px);
}
.service-tiles-grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  gap: 18px;
  padding: 28px 30px 28px 0;
}
.service-tiles-card-wrap {
  min-width: 0;
  display: flex;
}
.service-tiles-card-link {
  width: 100%;
  min-width: 0;
  display: flex;
  color: inherit;
  text-decoration: none;
}
.service-tiles-card {
  position: relative;
  width: 100%;
  min-height: var(--service-tiles-tile-min-height);
  display: grid;
  grid-template-rows: var(--service-tiles-tile-image-height) minmax(54px, auto);
  overflow: hidden;
  border: 1px solid rgba(19, 47, 45, 0.10);
  border-radius: 7px;
  background: var(--service-tiles-card-bg);
  box-shadow: 0 9px 22px rgba(19, 47, 45, 0.11);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}
.service-tiles-card-link:hover .service-tiles-card,
.service-tiles-card-link:focus-visible .service-tiles-card {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--tile-accent, var(--service-tiles-accent)) 28%, rgba(19, 47, 45, 0.10));
  box-shadow: 0 16px 30px rgba(19, 47, 45, 0.16);
}
.service-tiles-card-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--tile-accent, var(--service-tiles-accent)) 24%, transparent);
  outline-offset: 4px;
  border-radius: 7px;
}
.service-tiles-card-image-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
  height: var(--service-tiles-tile-image-height);
  overflow: hidden;
  background: #edf0ed;
}
.service-tiles-card-image,
.service-tiles-card-image-fallback {
  width: 100%;
  height: 100%;
  display: block;
}
.service-tiles-card-image {
  object-fit: cover;
  transform: scale(1);
  transition: transform 420ms ease;
}
.service-tiles-card-link:hover .service-tiles-card-image,
.service-tiles-card-link:focus-visible .service-tiles-card-image {
  transform: scale(1.055);
}
.service-tiles-card-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  color: var(--service-tiles-muted);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(180deg, #f6f7f4 0%, #e8ece6 100%);
}
.service-tiles-card-icon {
  position: absolute;
  z-index: 2;
  top: calc(var(--service-tiles-tile-image-height) - 26px);
  left: 18px;
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(19, 47, 45, 0.08);
  border-radius: 999px;
  background: #fffdf8;
  color: var(--tile-accent, var(--service-tiles-accent));
  box-shadow: 0 10px 22px rgba(19, 47, 45, 0.14);
  pointer-events: none;
}
.service-tiles-card-icon-node,
.service-tiles-card-icon-node svg,
.service-tiles-card-icon-image {
  width: 25px;
  height: 25px;
  display: block;
}
.service-tiles-card-icon-node svg {
  stroke-width: 2.2;
}
.service-tiles-card-icon-image {
  object-fit: contain;
}
.service-tiles-card-content {
  min-width: 0;
  display: flex;
  min-height: 54px;
  align-items: center;
  justify-content: center;
  padding: 18px 18px 14px;
  text-align: center;
}
.service-tiles-card[data-has-icon="true"] .service-tiles-card-content {
  padding-left: 70px;
}
.service-tiles-card-title {
  margin: 0;
  color: var(--service-tiles-text);
  font-size: 15px;
  line-height: 1.22;
  font-weight: 850;
  letter-spacing: 0;
}
.service-tiles-card[data-variant="default"][data-branded-overlay="true"] .service-tiles-card-content {
  background: color-mix(in srgb, var(--tile-accent, var(--service-tiles-accent)) 92%, #111827);
}
.service-tiles-card[data-variant="default"][data-branded-overlay="true"] .service-tiles-card-title {
  color: #ffffff;
}
.service-tiles-card-arrow {
  display: none;
}
.service-tiles-card[data-variant="overlay"],
.service-tiles-card[data-variant="popup"] {
  grid-template-rows: minmax(var(--service-tiles-tile-min-height), auto);
}
.service-tiles-card[data-variant="overlay"] .service-tiles-card-image-wrap,
.service-tiles-card[data-variant="popup"] .service-tiles-card-image-wrap {
  position: absolute;
  inset: 0;
  height: auto;
}
.service-tiles-card[data-variant="overlay"] .service-tiles-card-content {
  position: relative;
  z-index: 1;
  min-height: var(--service-tiles-tile-min-height);
  align-items: end;
  padding: 52px 18px 18px;
  color: #ffffff;
  background: linear-gradient(180deg, transparent 34%, rgba(0, 0, 0, 0.68) 100%);
}
.service-tiles-card[data-variant="overlay"] .service-tiles-card-title {
  color: #ffffff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
}
.service-tiles-card[data-variant="overlay"][data-branded-overlay="true"] .service-tiles-card-content {
  background: linear-gradient(180deg, transparent 28%, color-mix(in srgb, var(--tile-accent, var(--service-tiles-accent)) 76%, transparent) 100%);
}
.service-tiles-card[data-variant="popup"] .service-tiles-card-content {
  position: relative;
  z-index: 1;
  min-height: var(--service-tiles-tile-min-height);
  align-items: end;
  padding: 0;
}
.service-tiles-card[data-variant="popup"] .service-tiles-card-title {
  width: 100%;
  margin-top: auto;
  padding: 14px 16px;
  color: #ffffff;
  background: color-mix(in srgb, var(--tile-accent, var(--service-tiles-accent)) 90%, #111827);
  transform: translateY(64%);
  transition: transform 180ms ease;
}
.service-tiles-card-link:hover .service-tiles-card[data-variant="popup"] .service-tiles-card-title,
.service-tiles-card-link:focus-visible .service-tiles-card[data-variant="popup"] .service-tiles-card-title {
  transform: translateY(0);
}
@media (max-width: 900px) {
  .service-tiles-layout {
    grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1fr);
    gap: 24px;
  }
  .service-tiles-copy {
    padding: 34px 28px;
  }
  .service-tiles-title {
    font-size: 34px;
  }
  .service-tiles-grid {
    gap: 14px;
    padding: 24px 24px 24px 0;
  }
}
@media (max-width: 767px) {
  .service-tiles-shell {
    padding: 18px 12px;
  }
  .service-tiles-layout {
    display: block;
    overflow: visible;
    border-radius: 8px;
    padding: 26px 16px 24px;
  }
  .service-tiles-copy {
    padding: 0;
    background: transparent;
  }
  .service-tiles-overline {
    gap: 10px;
    margin-bottom: 14px;
    font-size: 10px;
  }
  .service-tiles-overline::before {
    flex-basis: 30px;
    width: 30px;
  }
  .service-tiles-title {
    max-width: 330px;
    font-size: 28px;
    line-height: 1.08;
  }
  .service-tiles-description {
    max-width: 340px;
    margin-top: 12px;
    font-size: 13px;
    line-height: 1.5;
  }
  .service-tiles-cta {
    width: 100%;
    min-height: 42px;
    margin-top: 22px;
    padding: 12px 18px;
    font-size: 12px;
  }
  .service-tiles-list {
    margin-top: 28px;
  }
  .service-tiles-list-heading {
    grid-template-columns: auto max-content minmax(20px, 1fr);
    gap: 10px;
    margin-bottom: 12px;
  }
  .service-tiles-list-heading-icon {
    width: 22px;
    height: 22px;
  }
  .service-tiles-list-heading-text {
    font-size: 10px;
  }
  .service-tiles-list-link {
    min-height: 32px;
    gap: 10px;
    font-size: 12px;
  }
  .service-tiles-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    padding: 22px 0 0;
  }
  .service-tiles-card {
    min-height: var(--service-tiles-mobile-tile-min-height);
    grid-template-columns: 84px minmax(0, 1fr) 24px;
    grid-template-rows: minmax(var(--service-tiles-mobile-tile-min-height), auto);
    align-items: stretch;
  }
  .service-tiles-card-image-wrap {
    height: 100%;
    min-height: var(--service-tiles-mobile-tile-min-height);
  }
  .service-tiles-card-content,
  .service-tiles-card[data-has-icon="true"] .service-tiles-card-content {
    min-height: var(--service-tiles-mobile-tile-min-height);
    justify-content: flex-start;
    padding: 12px 10px 12px 16px;
    text-align: left;
  }
  .service-tiles-card-title {
    font-size: 12px;
    line-height: 1.24;
  }
  .service-tiles-card-arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    color: var(--service-tiles-text);
  }
  .service-tiles-card-icon {
    top: 50%;
    left: 84px;
    width: 40px;
    height: 40px;
    border: 2px solid #ffffff;
    transform: translate(-50%, -50%);
  }
  .service-tiles-card-icon-node,
  .service-tiles-card-icon-node svg,
  .service-tiles-card-icon-image {
    width: 20px;
    height: 20px;
  }
  .service-tiles-card[data-variant="overlay"],
  .service-tiles-card[data-variant="popup"] {
    grid-template-columns: 84px minmax(0, 1fr) 24px;
  }
  .service-tiles-card[data-variant="overlay"] .service-tiles-card-image-wrap,
  .service-tiles-card[data-variant="popup"] .service-tiles-card-image-wrap {
    position: relative;
    inset: auto;
  }
  .service-tiles-card[data-variant="overlay"] .service-tiles-card-content,
  .service-tiles-card[data-variant="popup"] .service-tiles-card-content {
    min-height: var(--service-tiles-mobile-tile-min-height);
    align-items: center;
    padding: 12px 10px 12px 16px;
    background: transparent;
  }
  .service-tiles-card[data-variant="overlay"] .service-tiles-card-title {
    color: var(--service-tiles-text);
    text-shadow: none;
  }
  .service-tiles-card[data-variant="popup"] .service-tiles-card-title {
    width: auto;
    padding: 0;
    color: var(--service-tiles-text);
    background: transparent;
    transform: none;
  }
  .service-tiles-card[data-variant="default"][data-branded-overlay="true"] .service-tiles-card-content {
    background: transparent;
  }
  .service-tiles-card[data-variant="default"][data-branded-overlay="true"] .service-tiles-card-title {
    color: var(--service-tiles-text);
  }
}
@media (max-width: 380px) {
  .service-tiles-layout {
    padding-left: 14px;
    padding-right: 14px;
  }
  .service-tiles-card {
    grid-template-columns: 76px minmax(0, 1fr) 22px;
  }
  .service-tiles-card-icon {
    left: 76px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getImage(item: ServiceTilesItem): ServiceTilesImage {
  if (item.image) return item.image;

  const heroImage = item.heroImage as ServiceTilesImage | undefined;
  if (heroImage) return heroImage;

  const galleryImage = item.images?.[0] as ServiceTilesImage | undefined;
  if (galleryImage) return galleryImage;

  if (item.imageUrl) {
    return {
      src: item.imageUrl,
      alt: item.imageAlt || item.title || item.name,
    };
  }

  return {};
}

function getImageSrc(image: ServiceTilesImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: ServiceTilesImage, title: string): string {
  return image.alt || image.thumbnailAlt || image.originalAlt || image.title || title;
}

function getItemTitle(item: ServiceTilesItem): string {
  return item.title || item.name || "Service";
}

function getItemHref(item: ServiceTilesItem): string {
  return item.cta?.href || item.ctaHref || item.href || item.link || "#";
}

function getSectionText(service?: CategoryDetails, description?: string, text?: string) {
  if (text) return text;
  if (description) return description;
  if (Array.isArray(service?.description)) return service.description.join(" ");
  return undefined;
}

function resolveSectionCta({
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
}: Pick<ServiceTilesProps, "cta" | "ctaLabel" | "ctaHref" | "ctaAriaLabel">) {
  const label = cta?.label || ctaLabel;
  const href = cta?.href || ctaHref;

  if (!label || !href) return undefined;

  return {
    label,
    href,
    ariaLabel: cta?.ariaLabel || ctaAriaLabel,
    icon: cta?.icon,
  };
}

function getTileMinHeight(tileMinHeight: number | string | undefined, tileHeight: string | undefined, variant: TileOverlays) {
  if (tileMinHeight) return getCssSize(tileMinHeight);
  if (!tileHeight) return `${defaultTileMinHeight}px`;
  return variant === "default" ? `calc(${tileHeight} + 56px)` : tileHeight;
}

function ServiceTilesIcon({ item }: { item: ServiceTilesItem }) {
  if (item.iconSrc) {
    return <img src={item.iconSrc} alt={item.iconAlt || ""} className="service-tiles-card-icon-image" />;
  }

  if (item.icon) {
    return <span className="service-tiles-card-icon-node">{item.icon}</span>;
  }

  return null;
}

export default function ServiceTiles({
  service,
  items,
  serviceTileClasses,
  Link = DefaultLink,
  variant = "default",
  tileHeight,
  tileMinHeight,
  mobileTileMinHeight = defaultMobileTileMinHeight,
  tileImageHeight,
  brandedOverlay = false,
  overline,
  title,
  text,
  description,
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
  accentColor = defaultAccentColor,
  backgroundColor = defaultBackgroundColor,
  panelBackgroundColor = defaultPanelBackgroundColor,
  maxWidth = defaultMaxWidth,
  listTitle = "Our Services",
  className = "",
  ariaLabel,
}: ServiceTilesProps) {
  const resolvedItems = (items?.length ? items : service?.subCategories || []) as ServiceTilesItem[];
  const resolvedTitle = title || service?.long_name || service?.name || service?.label;
  const resolvedText = getSectionText(service, description, text);
  const resolvedCta = resolveSectionCta({ cta, ctaLabel, ctaHref, ctaAriaLabel });
  const resolvedTileImageHeight = getCssSize(tileImageHeight ?? tileHeight ?? defaultTileImageHeight);
  const resolvedTileMinHeight = getTileMinHeight(tileMinHeight, tileHeight, variant);

  if (!resolvedItems.length) return null;

  return (
    <section
      className={`service-tiles ${className}`}
      aria-label={ariaLabel || resolvedTitle || listTitle}
      style={
        {
          "--service-tiles-accent": accentColor,
          "--service-tiles-bg": backgroundColor,
          "--service-tiles-panel-bg": panelBackgroundColor,
          "--service-tiles-tile-image-height": resolvedTileImageHeight,
          "--service-tiles-tile-min-height": resolvedTileMinHeight,
          "--service-tiles-mobile-tile-min-height": getCssSize(mobileTileMinHeight),
        } as React.CSSProperties
      }
    >
      <style>{serviceTilesCss}</style>

      <div className="service-tiles-shell" style={{ maxWidth: getCssSize(maxWidth) }}>
        <div className="service-tiles-layout">
          <div className="service-tiles-copy">
            {overline ? <p className="service-tiles-overline">{overline}</p> : null}
            {resolvedTitle ? <h2 className="service-tiles-title">{resolvedTitle}</h2> : null}
            {resolvedText ? <p className="service-tiles-description">{resolvedText}</p> : null}

            {resolvedCta ? (
              <Link
                href={resolvedCta.href}
                className="service-tiles-cta"
                aria-label={resolvedCta.ariaLabel || resolvedCta.label}
              >
                {resolvedCta.icon ?? <ShieldCheck size={18} strokeWidth={2.25} aria-hidden="true" />}
                {resolvedCta.label}
              </Link>
            ) : null}

            <nav className="service-tiles-list" aria-label={listTitle}>
              <div className="service-tiles-list-heading">
                <span className="service-tiles-list-heading-icon" aria-hidden="true">
                  <ShieldCheck size={24} strokeWidth={2.15} />
                </span>
                <span className="service-tiles-list-heading-text">{listTitle}</span>
                <span className="service-tiles-list-heading-rule" aria-hidden="true" />
              </div>

              <ul className="service-tiles-list-items">
                {resolvedItems.map((item) => {
                  const itemTitle = getItemTitle(item);

                  return (
                    <li key={item.id}>
                      <Link href={getItemHref(item)} title={itemTitle} className="service-tiles-list-link">
                        <CheckCircle className="service-tiles-list-check" size={16} strokeWidth={2.2} aria-hidden="true" />
                        <span>{itemTitle}</span>
                        <ArrowRight className="service-tiles-list-arrow" size={15} strokeWidth={2.2} aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="service-tiles-grid" aria-label="Service tiles">
            {resolvedItems.map((item) => {
              const itemTitle = getItemTitle(item);
              const image = getImage(item);
              const imageSrc = getImageSrc(image);
              const itemAccent = item.accentColor || item.accentDarkColor || accentColor;
              const hasIcon = Boolean(item.icon || item.iconSrc);

              return (
                <div
                  key={item.id}
                  className={`service-tiles-card-wrap ${serviceTileClasses ?? ""}`}
                  style={{ "--tile-accent": itemAccent } as React.CSSProperties}
                >
                  <Link
                    href={getItemHref(item)}
                    title={itemTitle}
                    aria-label={item.cta?.ariaLabel || item.ctaAriaLabel || itemTitle}
                    className="service-tiles-card-link"
                  >
                    <article
                      className="service-tiles-card"
                      data-has-icon={hasIcon ? "true" : "false"}
                      data-variant={variant}
                      data-branded-overlay={brandedOverlay ? "true" : "false"}
                    >
                      <div className="service-tiles-card-image-wrap">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={getImageAlt(image, itemTitle)}
                            className="service-tiles-card-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="service-tiles-card-image-fallback">{itemTitle}</div>
                        )}
                      </div>

                      {hasIcon ? (
                        <span className="service-tiles-card-icon" aria-hidden="true">
                          <ServiceTilesIcon item={item} />
                        </span>
                      ) : null}

                      <div className="service-tiles-card-content">
                        <h3 className="service-tiles-card-title">{itemTitle}</h3>
                      </div>

                      <span className="service-tiles-card-arrow" aria-hidden="true">
                        <ArrowRight size={15} strokeWidth={2.2} />
                      </span>
                    </article>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
